import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Search, Copy, X, Link2, CheckSquare, Square, ClipboardPaste, Pencil, History, FileVideo, FileText as PdfIcon } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";
import ImageEditor from "./ImageEditor";

const fullUrl = (url) => url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
const fmtSize = (b) => b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`;
const isImg = (m) => (m.content_type || "").startsWith("image") || /\.(jpg|jpeg|png|webp|gif|svg|avif)$/i.test(m.url);

function AssignModal({ count, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [addToGallery, setAddToGallery] = useState(true);
  useEffect(() => {
    const t = setTimeout(async () => {
      const { data } = await api.get("/admin/products", { params: { q, limit: 8 } });
      setResults(data.products);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const assign = async (pid) => {
    if (busy) return;
    setBusy(true);
    try {
      await onDone(pid, addToGallery);
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="media-assign-modal">
      <div className="bg-white w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="font-serif text-xl">Assign {count} image(s) to product</p>
          <button onClick={onClose} aria-label="Close"><X size={20} /></button>
        </div>
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product by name or SKU…"
          data-testid="assign-product-search" className="tq-input mb-3" />
        <div className="max-h-56 overflow-y-auto border border-[#E5DEC9] divide-y divide-[#F4EFE6]">
          {results.map((p) => (
            <button key={p.id} onClick={() => assign(p.id)} disabled={busy} data-testid={`assign-product-${p.slug}`}
              className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#F7F2E7] flex items-center gap-3 disabled:opacity-50">
              {p.images?.[0]?.url && <img src={p.images[0].url} alt="" className="w-8 h-10 object-cover bg-[#F4EFE6]" />}
              <span><span className="block font-medium">{p.name}</span><span className="text-xs text-[#9a938a]">{p.sku}</span></span>
            </button>
          ))}
          {!results.length && <p className="p-4 text-sm text-[#9a938a]">No products found.</p>}
        </div>
        <label className="flex items-center gap-2 text-sm mt-4 cursor-pointer">
          <input type="checkbox" checked={addToGallery} onChange={(e) => setAddToGallery(e.target.checked)} data-testid="assign-add-to-gallery" className="accent-[#D4AF37] w-4 h-4" />
          Also add images to the product gallery
        </label>
      </div>
    </div>
  );
}

function DetailsModal({ item, onClose, onCopy, onDelete, onAssign, onEdit, onRestore }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="media-details-modal">
      <div className="bg-white w-full max-w-2xl p-6 grid sm:grid-cols-2 gap-6 max-h-[90vh] overflow-y-auto">
        <div className="bg-[#F4EFE6] flex items-center justify-center min-h-[240px] relative">
          <div className="absolute inset-0 animate-pulse bg-[#EDE7D9]" />
          {isImg(item)
            ? <img src={fullUrl(item.url)} alt={item.alt || item.filename} className="relative max-h-[50vh] w-auto object-contain" />
            : <p className="relative text-sm text-[#9a938a] p-8 text-center break-all">{item.filename}</p>}
        </div>
        <div className="min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="font-serif text-xl break-all">{item.filename}</p>
            <button onClick={onClose} aria-label="Close"><X size={20} /></button>
          </div>
          <dl className="mt-4 space-y-2.5 text-sm">
            <div><dt className="tq-overline !text-[10px]">Uploaded</dt><dd>{new Date(item.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</dd></div>
            <div><dt className="tq-overline !text-[10px]">File Size</dt><dd>{fmtSize(item.size)}</dd></div>
            <div><dt className="tq-overline !text-[10px]">Linked Product</dt>
              <dd data-testid="media-detail-product">{item.product ? <>{item.product.name} <span className="text-[#9a938a]">({item.product.sku})</span></> : <span className="text-[#9a938a]">Not linked</span>}</dd></div>
            <div><dt className="tq-overline !text-[10px]">Direct URL</dt>
              <dd className="flex gap-2 items-center"><span className="text-xs break-all text-[#6E6D6A] flex-1">{fullUrl(item.url)}</span></dd></div>
          </dl>
          <div className="flex flex-wrap gap-2 mt-5">
            {isImg(item) && <button onClick={onEdit} data-testid="media-detail-edit" className="tq-btn-dark !py-2 !px-4 !text-[10px]"><Pencil size={13} /> Edit Image</button>}
            <button onClick={() => onCopy(item)} data-testid="media-detail-copy" className="tq-btn-outline !py-2 !px-4 !text-[10px]"><Copy size={13} /> Copy URL</button>
            <button onClick={onAssign} data-testid="media-detail-assign" className="tq-btn-outline !py-2 !px-4 !text-[10px]"><Link2 size={13} /> Assign to Product</button>
            {item.original_url && <button onClick={onRestore} data-testid="media-detail-restore" className="tq-btn-outline !py-2 !px-4 !text-[10px]"><History size={13} /> Restore Original</button>}
            <button onClick={onDelete} data-testid="media-detail-delete" className="tq-btn-outline !py-2 !px-4 !text-[10px] !border-[#8B1E21] !text-[#8B1E21]"><Trash2 size={13} /> Delete</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminMedia() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [linked, setLinked] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [selected, setSelected] = useState([]);
  const [detail, setDetail] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/media", { params: { q, linked } }).then(({ data }) => setItems(data));
  useEffect(() => { load(); }, [q, linked]);

  const uploadFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/") || f.type === "application/pdf" || f.type.startsWith("video/"));
    if (!list.length || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      list.forEach((f) => fd.append("files", f));
      setProgress(`Uploading ${list.length} file(s)…`);
      const { data } = await api.post("/admin/media/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const failed = (data.items || []).filter((i) => i.error);
      toast.success(`${data.uploaded} file(s) uploaded${failed.length ? ` · ${failed.length} skipped (${failed[0].error})` : ""}`);
      load();
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setBusy(false); setProgress(""); if (fileRef.current) fileRef.current.value = ""; }
  };

  useEffect(() => {
    const onPaste = (e) => {
      const files = [...(e.clipboardData?.files || [])];
      if (files.length) { e.preventDefault(); uploadFiles(files); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  });

  const copyOne = (m) => { navigator.clipboard.writeText(fullUrl(m.url)); toast.success("URL copied"); };
  const copyBulk = () => {
    const urls = items.filter((m) => selected.includes(m.id)).map((m) => fullUrl(m.url)).join("\n");
    navigator.clipboard.writeText(urls);
    toast.success(`${selected.length} URL(s) copied — one per line, ready to paste into Excel/CSV`);
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} selected file(s)? Product galleries using them are not affected (they keep their own copies).`)) return;
    const { data } = await api.post("/admin/media/bulk-delete", { ids: selected });
    toast.success(`${data.deleted} file(s) deleted`);
    setSelected([]);
    setDetail(null);
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    await api.delete(`/admin/media/${id}`);
    toast.success("Deleted");
    setDetail(null);
    load();
  };

  const doAssign = async (pid, addToGallery) => {
    const ids = detail && !selected.length ? [detail.id] : selected.length ? selected : [detail.id];
    const { data } = await api.post("/admin/media/assign", { ids, product_id: pid, add_to_product: addToGallery });
    toast.success(`${data.linked} image(s) linked${data.added_to_gallery ? ` · ${data.added_to_gallery} added to product gallery` : ""}`);
    setAssigning(false);
    setSelected([]);
    load();
  };

  const restore = async (m) => {
    try {
      await api.post(`/admin/media/${m.id}/restore`);
      toast.success("Original image restored");
      setDetail(null);
      load();
    } catch (err) { toast.error(err.friendlyMessage); }
  };

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allSelected = items?.length > 0 && selected.length === items.length;

  return (
    <div data-testid="admin-media">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-serif text-3xl">Media Library</h1>
          <p className="text-sm text-[#6E6D6A] mt-1 flex items-center gap-1.5"><ClipboardPaste size={14} /> Tip: you can also paste images directly with Ctrl+V</p>
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="media-upload-btn" className="tq-btn-dark !py-2.5">
          <Upload size={14} /> {busy ? progress || "Uploading…" : "Upload Images (multi-select)"}
        </button>
        <input ref={fileRef} type="file" accept="image/*,application/pdf,video/*" multiple onChange={(e) => uploadFiles(e.target.files)} className="hidden" data-testid="media-upload-input" />
      </div>

      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="relative max-w-xs flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9a938a]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search filename, product, SKU or URL…" data-testid="media-search" className="tq-input !py-2.5 !pl-9" />
        </div>
        <select value={linked} onChange={(e) => setLinked(e.target.value)} data-testid="media-linked-filter" className="tq-input !py-2.5 !w-auto bg-white">
          <option value="">All files</option><option value="yes">Linked to products</option><option value="no">Not linked</option>
        </select>
        {items && <p className="text-xs text-[#9a938a]">{items.length} file(s)</p>}
      </div>

      {selected.length > 0 && (
        <div className="sticky top-0 z-20 bg-[#121212] text-white px-4 py-3 mb-4 flex flex-wrap items-center gap-3" data-testid="media-selection-bar">
          <span className="text-sm font-semibold">{selected.length} selected</span>
          <button onClick={copyBulk} data-testid="media-copy-urls" className="tq-btn !py-1.5 !px-3 !text-[10px] bg-[#D4AF37] text-[#121212]"><Copy size={12} /> Copy URLs</button>
          <button onClick={() => setAssigning(true)} data-testid="media-assign-selected" className="tq-btn !py-1.5 !px-3 !text-[10px] bg-white text-[#121212]"><Link2 size={12} /> Assign to Product</button>
          <button onClick={bulkDelete} data-testid="media-bulk-delete" className="tq-btn !py-1.5 !px-3 !text-[10px] bg-[#8B1E21] text-white"><Trash2 size={12} /> Delete Selected</button>
          <button onClick={() => setSelected([])} data-testid="media-clear-selection" className="text-xs text-white/70 underline ml-auto">Clear</button>
        </div>
      )}

      {!items ? <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">{[...Array(5)].map((_, i) => <div key={i} className="aspect-square bg-white animate-pulse" />)}</div>
        : items.length === 0 ? (
          <div className="bg-white border border-[#E5DEC9] p-12 text-center text-[#6E6D6A]">No files found. Upload multiple images at once or paste them with Ctrl+V.</div>
        ) : (
          <>
            <button onClick={() => setSelected(allSelected ? [] : items.map((m) => m.id))} data-testid="media-select-all"
              className="flex items-center gap-2 text-xs font-semibold mb-3 text-[#6E6D6A] hover:text-[#121212]">
              {allSelected ? <CheckSquare size={15} className="text-[#D4AF37]" /> : <Square size={15} />} Select all
            </button>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {items.map((m) => {
                const isSel = selected.includes(m.id);
                return (
                  <div key={m.id} className={`bg-white border group relative transition-colors ${isSel ? "border-[#D4AF37] ring-2 ring-[#D4AF37]/40" : "border-[#E5DEC9]"}`} data-testid={`media-item-${m.id}`}>
                    <button onClick={() => toggle(m.id)} aria-label={`Select ${m.filename}`} data-testid={`media-select-${m.id}`}
                      className={`absolute top-2 left-2 z-10 w-6 h-6 flex items-center justify-center rounded-sm transition-colors ${isSel ? "bg-[#D4AF37] text-[#121212]" : "bg-white/90 text-[#9a938a] opacity-0 group-hover:opacity-100"} ${isSel ? "!opacity-100" : ""}`}>
                      {isSel ? <CheckSquare size={15} /> : <Square size={15} />}
                    </button>
                    <button onClick={() => setDetail(m)} className="block w-full" aria-label={`Preview ${m.filename}`}>
                      {isImg(m)
                        ? <img src={fullUrl(m.url)} alt={m.alt || m.filename} className="w-full aspect-square object-cover" loading="lazy" />
                        : <div className="w-full aspect-square flex flex-col items-center justify-center gap-2 text-xs text-[#9a938a] bg-[#F4EFE6] p-2 text-center">
                            {(m.content_type || "").startsWith("video") ? <FileVideo size={28} className="text-[#C5A059]" /> : <PdfIcon size={28} className="text-[#C5A059]" />}
                            <span className="break-all line-clamp-2">{m.filename}</span>
                          </div>}
                    </button>
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {isImg(m) && <button onClick={() => setEditing(m)} aria-label="Edit image" data-testid={`media-edit-${m.id}`} className="p-1.5 bg-white shadow text-[#121212] hover:text-[#C5A059]"><Pencil size={13} /></button>}
                      <button onClick={() => copyOne(m)} aria-label="Copy URL" data-testid={`media-copy-${m.id}`} className="p-1.5 bg-white shadow text-[#121212] hover:text-[#C5A059]"><Copy size={13} /></button>
                      <button onClick={() => del(m.id)} aria-label="Delete" data-testid={`media-delete-${m.id}`} className="p-1.5 bg-white shadow text-[#8B1E21]"><Trash2 size={13} /></button>
                    </div>
                    <div className="p-1.5">
                      <p className="text-[10px] text-[#9a938a] truncate">{m.filename}</p>
                      {m.product && <p className="text-[9px] text-[#1A3C34] font-semibold truncate">→ {m.product.name}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      {detail && <DetailsModal item={detail} onClose={() => setDetail(null)} onCopy={copyOne}
        onDelete={() => del(detail.id)} onAssign={() => setAssigning(true)}
        onEdit={() => { setEditing(detail); setDetail(null); }} onRestore={() => restore(detail)} />}
      {assigning && <AssignModal count={selected.length || 1} onClose={() => setAssigning(false)} onDone={doAssign} />}
      {editing && <ImageEditor item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
